/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/KanbanBoardView" />
import { ITeam } from "Agile/Contracts/Common";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IVssTelemetryService, publishEvent } from "VSS/Platform/Telemetry";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabContent } from "VSSUI/Tabs";
import { KanbanBoard } from "Wit/BoardsHub/KanbanBoard/Components/KanbanBoard";
import { KanbanBoardFilterBar } from "Wit/BoardsHub/KanbanBoard/Components/KanbanBoardFilterBar";
import { AnalyticsTabName, BoardTabName, BoardsHeaderDataProvider, KanbanBoardHeader } from "Wit/BoardsHub/KanbanBoard/Components/KanbanBoardHeader";
import { IKanbanBoardService } from "Wit/BoardsHub/KanbanBoard/IKanbanBoardService";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";
import { IBoardsHubContentHeaderData } from "Wit/Common/AgileControls/AgileContracts";
import { BacklogAnalyticsView } from "Wit/Common/AgileControls/BacklogAnalyticsView";
import { IAutomationRulesService } from "Wit/Common/AutomationRulesService/AutomationRulesService";
import { WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature } from "Wit/Common/Constants/CustomerInteligence";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { AgileBacklogPermissionProvider } from "Wit/Common/Controls/Boards/AgileBacklogPermissionContext";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IHubErrorProps } from "Wit/Common/HubError/Components/HubError.props";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { IWorkItemFormDialogService } from "Wit/Common/WorkItemDialogs/Services/IWorkItemFormDialogService";
import { DirectoryItem } from "Wit/DirectoryService/Client/Contracts/Directory";
import { IDirectoryDropdownItem } from "Wit/DirectoryService/Models";
import {
    AssignedToFilterValueProvider,
    IterationPathFilterValueProvider,
    ParentItemFilterValueProvider,
    StateFilterValueProvider,
    WorkItemTypeFilterValueProvider
} from "Wit/Filter/FilterValueProviders";
import { TextFilterProvider } from "Wit/Filter/TextFilterProvider";
import { DataSourceFilterValueProvider, IWorkItemFilterField, WorkItemFilterFieldType } from "Wit/Filter/WorkItemFilter";
import { BoardsMessages, ContainerNames } from "Wit/Messages/Components/BoardsMessages";
import { IWorkItemEventData, IWorkItemEventService, IWorkItemEventType } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import * as React from "react";
import { isEmbedded, workItemFormDialogSaveCompleteListener } from "./Util";

interface IKanbanBoardViewState {
    headerData: IBoardsHubContentHeaderData | undefined;
    loading: boolean;
    selectedBacklogLevel?: string;
    selectedTeam?: DirectoryItem;
}

interface IProductBacklogTabContext {
    level: string;
    workItemTypes: string[];
    team: ITeam;
    foregroundInstance: boolean;
}

const performanceArea = "Board";
const performanceFeature = "boards-hub";
const teamMRUKey = "Agile/Team";
const teamScopeName = "WebTeam";
const MruBacklogKey = "Agile/BoardsHub/BacklogId";

export class KanbanBoardView extends TfsComponent<never, IKanbanBoardViewState> {
    private analyticsLastRefreshed: ObservableValue<Date>;
    private filterFields: IWorkItemFilterField[];
    private filterToggled = new ObservableValue(false);
    private hasBacklogManagementPermission: boolean;
    private pivotContext: IProductBacklogTabContext;
    private selectedTabId = new ObservableValue(BoardTabName);
    private tabs = new ObservableCollection<IVssContributedTab>();
    private scrollableContainer = React.createRef<HTMLDivElement>();
    private _automationRulesSerivce: IAutomationRulesService;
    private _workItemEventService: IWorkItemEventService;
    private _isLoadingFirstTime = true;

    private TelemetryArea = "Boards";
    private ParentPageName = "NewBoardsHub-KanbanBoard";

    public constructor(props: never, context: IVssComponentContext) {
        super(props, context);

        let selectedTeam: DirectoryItem | undefined;
        if (this.teamData) {
            selectedTeam = { id: this.teamData.id, name: this.teamData.name, url: "" } as DirectoryItem;
        }

        const boardService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();
        const selectedBacklogLevel = historyState["backlogLevel"];
        if (historyState.pivot) {
            this.selectedTabId.value = historyState.pivot;
        }

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const headerData = contributionService.getData<IBoardsHubContentHeaderData>(BoardsHeaderDataProvider);

        this.pivotContext = {
            level: selectedBacklogLevel,
            workItemTypes: boardService.boardLayout.value?.backlogLevelWorkItemTypeColorAndIcons.map(type => type.workItemTypeName) ?? [],
            team: {
                id: this.teamData?.id ?? "",
                name: this.teamData?.name ?? ""
            },
            foregroundInstance: true
        };

        this.updateContributedTabs();
        this.filterFields = this.getFilterFields();
        this.hasBacklogManagementPermission = this.hasAgileToolsBacklogManagementPermission();

        this.state = {
            headerData,
            loading: false,
            selectedBacklogLevel,
            selectedTeam
        };

        this.analyticsLastRefreshed = new ObservableValue(new Date());
        const isTeamAutomationRulesEnabled = isFeatureFlagEnabled(context.pageContext, FeatureFlags.TeamAutomationRulesEnabled, false);
        if (isTeamAutomationRulesEnabled) {
            this._automationRulesSerivce = this.context.pageContext.getService<IAutomationRulesService>("IAutomationRulesService");
        }
        this._workItemEventService = this.context.pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._isLoadingFirstTime = false;
        const workItemFormDialogService = this.context.pageContext.getService<IWorkItemFormDialogService>("IWorkItemFormDialogService");
        workItemFormDialogService.subscribeToHistoryService(workItemFormDialogSaveCompleteListener);
        this.setDocumentTitle();

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature.NEW_BOARDS_PLATFORM, {
            source: "Boards"
        });

        if (isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.TeamAutomationRulesEnabled, false)) {
            this._workItemEventService.subscribe(this.onWorkItemSavedCallback.bind(this), IWorkItemEventType.WorkItemSaved);
        }
    }

    public componentDidUpdate(prevProps: never, prevState: IKanbanBoardViewState): void {
        super.componentDidUpdate(prevProps, prevState);

        // reset filter fields when board is switched to a new view
        if (prevState.selectedBacklogLevel !== this.state.selectedBacklogLevel || prevState.selectedTeam !== this.state.selectedTeam) {
            this.filterFields = this.getFilterFields();
            this.setDocumentTitle();
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        if (isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.TeamAutomationRulesEnabled, false)) {
            this._workItemEventService.unsubscribe(this.onWorkItemSavedCallback.bind(this), IWorkItemEventType.WorkItemSaved);
        }
    }

    public render(): JSX.Element {
        const { loading, selectedBacklogLevel, selectedTeam } = this.state;
        const boardService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
        const boardLayout = boardService.boardLayout;
        const boardSettings = boardService.boardSettingsData;
        const hasError = this.state.headerData?.exceptionInfo;
        return (
            <AgileBacklogPermissionProvider hasBacklogManagementPermission={this.hasBacklogManagementPermission}>
                <Surface background={SurfaceBackground.neutral}>
                    <Page className="flex-grow flex-column" selectedTabId={this.selectedTabId} tabProviders={this.tabs}>
                        <BoardsMessages className="boards-message-bar flex-noshrink" containerName={ContainerNames.BoardTab}/>
                        <KanbanBoardHeader
                            filterToggled={this.filterToggled}
                            headerData={this.state.headerData}
                            onChangeBacklogLevel={this.onSelectBacklogLevel}
                            onSelectedTabChanged={this.onSelectTab}
                            onSelectTeam={this.onSelectTeam}
                            onSettingsSaved={this.reloadBoardData}
                            onBeforeSettingsSaved={this.onBeforeSettingsSaved}
                            selectedBacklogLevel={selectedBacklogLevel}
                            selectedTab={this.selectedTabId}
                            teamSelection={selectedTeam}
                            isFiltering={boardService.isFiltering}
                            hasError={Boolean(hasError)}
                        />
                        {hasError ? (
                            <WrappedComponent<IHubErrorProps>
                                dependencies={["ms.vss-work-web.hub-error"]}
                                wrappedProps={{
                                    exceptionsInfo: [this.state.headerData!.exceptionInfo]
                                }}
                                wrappedType="ms.vss-work-web.hub-error-component"
                            />
                        ) : (
                            <>
                                <Observer boardSettings={boardSettings} selectedTabId={this.selectedTabId}>
                                    {({ boardSettings, selectedTabId }) => (
                                        <>
                                            {selectedTabId === BoardTabName && (
                                                <ConditionalChildren renderChildren={boardService.getWorkItemsLoaded()}>
                                                    <KanbanBoardFilterBar
                                                        className="padding-16 margin-horizontal-16"
                                                        boardId={boardSettings?.boardData.boardModel.board.id}
                                                        dataSource={boardService.filterDataSource}
                                                        fields={this.filterFields}
                                                        filter={boardService.filter}
                                                        filterToggled={this.filterToggled}
                                                    />
                                                </ConditionalChildren>
                                            )}
                                        </>
                                    )}
                                </Observer>
                                <div className="flex-row flex-grow v-scroll-auto" ref={this.scrollableContainer}>
                                    <div className="board-wrapper page-content-top flex-column flex-grow">
                                        <Observer selectedTabId={this.selectedTabId}>
                                            {({ selectedTabId }) => {
                                                if (loading) {
                                                    return <Spinner className="flex-grow" size={SpinnerSize.large} />;
                                                } else if (selectedTabId === BoardTabName) {
                                                    return (
                                                        <KanbanBoard
                                                            boardLayout={boardLayout}
                                                            boardSettings={boardSettings}
                                                            onMarkTTI={this.completeBoardTTIScenario}
                                                            scrollableData={{
                                                                scrollableContainer: this.scrollableContainer,
                                                                scrollableSettings: { debounceTime: 20, minYScrollDistance: 125 }
                                                            }}
                                                            onToggleFilter={toggled => (this.filterToggled.value = toggled)}
                                                        />
                                                    );
                                                } else if (selectedTabId === AnalyticsTabName) {
                                                    return (
                                                        <div className="page-content-top page-content flex-grow flex-row h-scroll-hidden">
                                                            <BacklogAnalyticsView
                                                                teamId={this.teamData?.id ?? ""}
                                                                teamName={this.teamData?.name ?? ""}
                                                                projectId={this.projectData?.id ?? ""}
                                                                categoryReferenceName={
                                                                    this.state.headerData?.visibleBacklogLevels.find(
                                                                        level => level.name === this.state.selectedBacklogLevel
                                                                    )?.id ?? ""
                                                                }
                                                                categoryName={this.state.selectedBacklogLevel ?? ""}
                                                                lastRefresh={this.analyticsLastRefreshed}
                                                                onLoadCompleted={() => {}}
                                                            />
                                                        </div>
                                                    );
                                                } else {
                                                    return (
                                                        <div className="page-content-top page-content flex-grow flex-row h-scroll-hidden">
                                                            <TabContent />
                                                        </div>
                                                    );
                                                }
                                            }}
                                        </Observer>
                                    </div>
                                </div>
                            </>
                        )}
                    </Page>
                </Surface>
            </AgileBacklogPermissionProvider>
        );
    }

    private startBoardTTIScenario(properties?: { [key: string]: any }) {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const scenario = performanceService.startScenario(performanceFeature, true);
        if (properties) {
            scenario.addProperties(properties);
        }
    }

    private completeBoardTTIScenario = () => {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(performanceArea, performanceFeature, undefined, {
            tab: this.selectedTabId.value
        });
    };

    private updateContributedTabs() {
        this.tabs = new ObservableCollection();
        if (!isEmbedded()) {
            this.tabs.push(getTabs(this.context.pageContext, "ms.vss-work-web.product-backlog-tabs", { contextualData: this.pivotContext }));
        }
    }

    private onSelectBacklogLevel = async (selectedBacklogLevel: string): Promise<void> => {
        if (!this._isLoadingFirstTime){
            this.startBoardTTIScenario({
                inPageNavigation: true,
                switchBacklog: true,
                tab: this.selectedTabId.value
            });
        }

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        state["backlogLevel"] = selectedBacklogLevel;
        historyService.pushState(state);
        this.setState({ loading: true, selectedBacklogLevel });

        const boardService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
        await boardService.reloadBoardData();

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        await settingsService.setEntries({ [MruBacklogKey]: selectedBacklogLevel }, SettingsUserScope.Me, teamScopeName, this.teamData?.id);

        this.updateContributionContext();
        this.setState({ loading: false });

        // Publish telemetry when user select work item from dropdown
        publishEvent(this.context.pageContext, this.TelemetryArea, "AnalyticsTab", {
            Scenario: "DropDownClick-WorkItemType",
            TeamId: this.teamData?.id,
            ParentPageName: this.ParentPageName
        });
    };

    private onSelectTab = (tabId: string) => {
        if (!this._isLoadingFirstTime){
            this.startBoardTTIScenario({
                inPageNavigation: true,
                switchTab: true,
                tab: tabId
            });
        }
        
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        if (state.pivot !== tabId) {
            state.pivot = tabId;
            historyService.pushState(state);
            this.selectedTabId.value = tabId;

            // publish when user clicks on Analytics Tab
            if(tabId == AnalyticsTabName){
                publishEvent(this.context.pageContext, this.TelemetryArea, "AnalyticsTab", {
                    ParentPageName: this.ParentPageName,
                    Scenario: "AnalyticsTabAccess"
                })
            }
        }
    };

    private onSelectTeam = async (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<IDirectoryDropdownItem>): Promise<void> => {
        if (!this._isLoadingFirstTime) {
            this.startBoardTTIScenario({
                inPageNavigation: true,
                switchTeam: true,
                tab: this.selectedTabId.value
            });
        }
        const selectedTeam = item.data?.directory;
        if (!selectedTeam || selectedTeam.id === this.state.selectedTeam?.id) {
            return;
        }

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        state["teamName"] = selectedTeam.name;
        historyService.pushState(state);

        this.setState({ loading: true });
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const headerData = await contributionService.getDataAsync<IBoardsHubContentHeaderData>(
            BoardsHeaderDataProvider,
            /* requestParameters */ undefined,
            /* ignoreCache */ true
        );

        // set MRU team setting since switching teams doesn't fire an FPS
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        settingsService.setEntries({ [teamMRUKey]: selectedTeam.id }, SettingsUserScope.Me, "project", this.projectData?.id);

        // clean up history state when switching between boards (don't want filter to persist through board switches)
        const historyState = historyService.getState();
        for (const field of this.filterFields) {
            delete historyState[field.fieldName];
        }
        historyService.replaceState(historyState);

        const boardService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
        await boardService.reloadBoardData();
        this.updateContributionContext(selectedTeam.id, selectedTeam.name);
        this.updateContributedTabs();

        const teamSettingsPanelService = this.context.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        teamSettingsPanelService.getTeamSettingsObservable({ ignoreCache: true });

        this.setState({ headerData, loading: false, selectedTeam });
    };

    private onBeforeSettingsSaved = async (): Promise<void> => {
        if (!isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.KanbanBoardSignalREventsUnwatchDisabled, false)) {
            const boardService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
            await boardService.unwatchSignalR();
        }
    }

    private reloadBoardData = async (): Promise<void> => {
        this.setState({ loading: true });

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const headerData = await contributionService.getDataAsync<IBoardsHubContentHeaderData>(
            BoardsHeaderDataProvider,
            /* requestParameters */ undefined,
            /* ignoreCache */ true
        );

        const boardService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
        await boardService.reloadBoardData();
        this.setState({ headerData, loading: false });
    };

    private hasAgileToolsBacklogManagementPermission(): boolean {
        const pageDataService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const permissionData = pageDataService.getData<{ hasPermission: boolean }>("ms.vss-work-web.backlog-permission-data-provider");
        return !!permissionData && permissionData.hasPermission;
    }

    private setDocumentTitle(): void {
        document.title = format(Resources.BoardsDocumentTitle, this.state.selectedTeam?.name, this.state.selectedBacklogLevel);
    }

    private updateContributionContext(teamId?: string, teamName?: string): void {
        const boardService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
        this.pivotContext.level = this.state.selectedBacklogLevel ?? "";
        this.pivotContext.workItemTypes =
            boardService.boardLayout.value?.backlogLevelWorkItemTypeColorAndIcons.map(type => type.workItemTypeName) ?? [];
        this.pivotContext.team = { name: teamId ?? this.state.selectedTeam?.name ?? "", id: teamName ?? this.state.selectedTeam?.id ?? "" };
    }

    private getFilterFields(): IWorkItemFilterField[] {
        const boardService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
        const filterDataSource = boardService.filterDataSource;
        const projectId = this.projectData?.id ?? "";
        return [
            {
                displayType: WorkItemFilterFieldType.Text,
                fieldName: TextFilterProvider.PROVIDER_TYPE,
                placeholder: Resources.FilterByKeyword
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.WorkItemType,
                placeholder: Resources.FilterByTypes,
                noItemsText: Resources.FilterNoTypes,
                valueProvider: new WorkItemTypeFilterValueProvider(projectId, filterDataSource)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.AssignedTo,
                placeholder: Resources.FilterByAssignedTo,
                valueProvider: new AssignedToFilterValueProvider(this.context.pageContext, filterDataSource)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.State,
                placeholder: Resources.FilterByStates,
                noItemsText: Resources.FilterNoStates,
                valueProvider: new StateFilterValueProvider(projectId, filterDataSource)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.AreaPath,
                placeholder: Resources.FilterByAreaPath,
                noItemsText: Resources.FilterNoArea,
                valueProvider: new DataSourceFilterValueProvider(CoreFieldRefNames.AreaPath, filterDataSource)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.IterationPath,
                placeholder: Resources.FilterByIteration,
                noItemsText: Resources.FilterNoIterations,
                valueProvider: new IterationPathFilterValueProvider(
                    this.context.pageContext,
                    projectId,
                    filterDataSource,
                    /** addCurrentIterationMacro */ true
                )
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.Tags,
                placeholder: Resources.FilterByTags,
                noItemsText: Resources.FilterNoTags,
                valueProvider: new DataSourceFilterValueProvider(CoreFieldRefNames.Tags, filterDataSource),
                showOrAndOperators: true
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.Parent,
                placeholder: Resources.FilterByParent,
                noItemsText: Resources.FilterNoParent,
                valueProvider: new ParentItemFilterValueProvider(this.context.pageContext, filterDataSource)
            }
        ] as IWorkItemFilterField[];
    }

    private async onWorkItemSavedCallback({ workItem }: IWorkItemEventData): Promise<void> {
        await this._automationRulesSerivce.evaluateAutomationRules(workItem);

        if (workItem.hasStateChanged()) {
            const boardService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
            boardService.refreshBoardLayout();
        }
    }
}

Components.add("kanban-board-view-component", KanbanBoardView);
